import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Pagination, Dropdown, Modal, InlineNotification } from '@carbon/react';
import { Add, TrashCan, Edit, View, Temperature, Humidity, Settings, WarningFilled, List, Grid as GridIcon } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { formatRelativeTime } from '../lib/timeUtils';
import { deleteZone } from '../services/zoneService';
import SensorStatusIndicator from '../components/SensorStatusIndicator';
import '../components/SensorStatusIndicator.css';

const ZoneListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { zones, isLoading: zonesLoading, error: zonesError } = useZones();
  const { sensors } = useSensorsOptimized();
  const { hasActiveSubscription } = useSubscription();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [zoneTypeFilter, setZoneTypeFilter] = useState('all'); // 'all', 'truck', 'cold_room', 'refrigerator', 'esky', 'office', 'room', 'other'
  
  // Pagination state (only used for table view)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Helper function to format temperature
  const formatTemperature = (temp, unit = 'celsius') => {
    if (temp === null || temp === undefined) return '--.-°C';
    if (unit === 'fahrenheit') {
      const fahrenheit = (temp * 9/5) + 32;
      return `${fahrenheit.toFixed(1)}°F`;
    }
    return `${temp.toFixed(1)}°C`;
  };

  // Helper function to format creation date
  const formatCreatedDate = (createdAt) => {
    if (!createdAt) return 'Unknown';
    
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now - createdDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffDays < 1) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 1) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins < 1 ? 'Just now' : `${diffMins} min ago`;
      }
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffMonths < 1) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    }
  };

  // Calculate zone statistics
  const zonesWithStats = useMemo(() => {
    return zones.map(zone => {
      // Get sensors for this zone
      const zoneSensors = sensors.filter(sensor => {
        // Check if sensor's zone_id matches, or if zone has sensor_ids array
        if (sensor.zone_id === zone.id) return true;
        if (zone.sensor_ids && Array.isArray(zone.sensor_ids)) {
          return zone.sensor_ids.includes(sensor.id);
        }
        return false;
      });

      // Calculate averages and ranges from sensors
      let avgTemp = null;
      let avgHumidity = null;
      let tempCount = 0;
      let humidityCount = 0;
      const tempValues = [];
      const humidityValues = [];

      zoneSensors.forEach(sensor => {
        if (sensor.latest_reading) {
          if (sensor.latest_reading.temperature !== null && sensor.latest_reading.temperature !== undefined) {
            const temp = sensor.latest_reading.temperature;
            avgTemp = (avgTemp || 0) + temp;
            tempValues.push(temp);
            tempCount++;
          }
          if (sensor.latest_reading.humidity !== null && sensor.latest_reading.humidity !== undefined && sensor.latest_reading.humidity > 0) {
            const humidity = sensor.latest_reading.humidity;
            avgHumidity = (avgHumidity || 0) + humidity;
            humidityValues.push(humidity);
            humidityCount++;
          }
        }
      });

      if (tempCount > 0) avgTemp = avgTemp / tempCount;
      if (humidityCount > 0) avgHumidity = avgHumidity / humidityCount;

      // Get temperature unit from first sensor (or default to celsius)
      const tempUnit = zoneSensors.length > 0 && zoneSensors[0].temperature_unit ? zoneSensors[0].temperature_unit : 'celsius';

      // Calculate temperature range (min to max) from all sensors in the zone
      let tempRange = 'N/A';
      if (tempValues.length > 0) {
        const minTemp = Math.min(...tempValues);
        const maxTemp = Math.max(...tempValues);
        tempRange = `${formatTemperature(minTemp, tempUnit)} to ${formatTemperature(maxTemp, tempUnit)}`;
      }

      // Calculate alerts (sensors with old readings or out of range)
      let alerts = 0;
      zoneSensors.forEach(sensor => {
        if (sensor.latest_reading) {
          const readingDate = new Date(sensor.latest_reading.recorded_at);
          const now = new Date();
          const diffMins = Math.floor((now - readingDate) / 60000);
          // If negative minutes (timestamp in future due to timezone/clock skew), treat as offline/alert
          if (diffMins < 0 || diffMins > 60) alerts++;
        } else {
          alerts++;
        }
      });

        // Collect sensor names and last readings
        const sensorNames = zoneSensors.map(sensor => sensor.name);
        const lastReadings = zoneSensors
          .filter(sensor => sensor.latest_reading)
          .map(sensor => ({
            sensorName: sensor.name,
            temperature: sensor.latest_reading.temperature,
            humidity: sensor.latest_reading.humidity,
            recordedAt: sensor.latest_reading.recorded_at
          }))
          .sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt))
          .slice(0, 3); // Show last 3 readings

        // Build full address - prefer address_formatted, fallback to building from parts
        const fullAddress = zone.address_formatted || 
          [zone.street_address, zone.city, zone.state, zone.zip_code]
            .filter(Boolean)
            .join(', ') || 
          zone.description || '';

        // Calculate zone temperature trend from sensor trends
        // Aggregate sensor trends: if majority rising -> rising, majority falling -> falling, else steady
        let tempTrend = 'STEADY';
        let humidityTrend = 'STEADY';
        
        const sensorTempTrends = zoneSensors
          .map(s => s.temperature_trend || s.temperature_status)
          .filter(Boolean);
        const sensorHumidityTrends = zoneSensors
          .map(s => s.humidity_trend || s.humidity_status)
          .filter(Boolean);
        
        if (sensorTempTrends.length > 0) {
          const risingCount = sensorTempTrends.filter(t => t === 'RISING' || t === 'HIGH').length;
          const fallingCount = sensorTempTrends.filter(t => t === 'FALLING' || t === 'LOW').length;
          if (risingCount > fallingCount) tempTrend = 'RISING';
          else if (fallingCount > risingCount) tempTrend = 'FALLING';
        }
        
        if (sensorHumidityTrends.length > 0) {
          const risingCount = sensorHumidityTrends.filter(t => t === 'RISING' || t === 'HIGH').length;
          const fallingCount = sensorHumidityTrends.filter(t => t === 'FALLING' || t === 'LOW').length;
          if (risingCount > fallingCount) humidityTrend = 'RISING';
          else if (fallingCount > risingCount) humidityTrend = 'FALLING';
        }

        return {
          id: zone.id,
          name: zone.name,
          description: zone.description || '',
          address: fullAddress, // Full formatted address
          devices: zoneSensors.length,
          avgTemp: avgTemp !== null ? formatTemperature(avgTemp, tempUnit) : '--.-°C',
          avgHumidity: avgHumidity !== null && avgHumidity > 0 ? `${avgHumidity.toFixed(1)}%` : 'No data',
          tempRange: tempRange,
          tempTrend,
          humidityTrend,
          alerts,
          color: zone.color || '#0f62fe',
          createdAt: zone.created_at,
          tags: sensorNames,
          lastReadings, // Add last readings to display on card
          zone, // Keep full zone object
        };
    });
  }, [zones, sensors]);

  const filteredZones = zonesWithStats.filter(zone => {
    const matchesSearch = zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      zone.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = zoneTypeFilter === 'all' || 
      zone.zone?.type === zoneTypeFilter || 
      zone.zone?.zone_type === zoneTypeFilter;
    return matchesSearch && matchesType;
  });

  // Pagination calculations (only for table view)
  const totalItems = filteredZones.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedZones = viewMode === 'table' 
    ? filteredZones.slice(startIndex, endIndex)
    : filteredZones;

  // Reset to page 1 when search term, view mode, or zone type filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode, zoneTypeFilter]);

  // Handle delete button click
  const handleDeleteClick = (zone) => {
    setZoneToDelete(zone);
    setDeleteModalOpen(true);
  };

  // Handle actual zone deletion
  const handleConfirmDelete = async () => {
    if (!zoneToDelete) return;

    setIsDeleting(true);
    try {
      await deleteZone(zoneToDelete.id);
      
      // Show success notification
      setNotification({
        kind: 'success',
        title: 'Zone deleted',
        subtitle: `${zoneToDelete.name} has been successfully deleted. All sensors have been unassigned.`,
      });

      // Close modal
      setDeleteModalOpen(false);
      setZoneToDelete(null);

      // Force page refresh to show updated zones list
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Small delay to show notification
    } catch (err) {
      console.error('Failed to delete zone:', err);
      setNotification({
        kind: 'error',
        title: 'Delete failed',
        subtitle: err.message || 'Failed to delete zone. Please try again.',
      });
      
      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setZoneToDelete(null);
  };

  if (zonesLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="zone-list-page">
        <div style={{ textAlign: 'center', color: textSecondary }}>
          <p>Loading zones...</p>
        </div>
      </div>
    );
  }

  if (zonesError) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }} data-testid="zone-list-page">
        <div style={{ textAlign: 'center', color: '#da1e28' }}>
          <p>Error loading zones: {zonesError}</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      feature="zone management and monitoring"
      disabled={!hasActiveSubscription}
      showChildrenGreyed={true}
      customMessage="You need an active subscription to access zone details and manage your monitoring zones. Your zone data is preserved and will be accessible once you resubscribe."
    >
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="zone-list-page">
      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        danger
        modalHeading="Delete zone"
        modalLabel="Confirm deletion"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={handleCancelDelete}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={isDeleting}
        size="sm"
      >
        <p style={{ marginBottom: '16px' }}>
          Are you sure you want to delete <strong>{zoneToDelete?.name}</strong>?
        </p>
        <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '12px' }}>
          This action cannot be undone. The zone will be permanently deleted.
        </p>
        {zoneToDelete?.devices > 0 && (
          <p style={{ color: '#ff832b', fontSize: '0.875rem', fontWeight: 600 }}>
            ⚠️ Note: {zoneToDelete.devices} sensor{zoneToDelete.devices > 1 ? 's' : ''} will be unassigned from this zone.
          </p>
        )}
      </Modal>

      {/* Notification */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '80px', 
          right: '16px', 
          zIndex: 9999,
          maxWidth: '400px'
        }}>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '4px' }}>Zones</h1>
                  <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{filteredZones.length} of {zonesWithStats.length} zones</p>
                </div>
                <Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/zones/add')} data-testid="add-zone">
                  Add Zone
                </Button>
              </div>
              
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'nowrap' }}>
                  <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                    <Search
                      labelText="Search"
                      placeholder="Search zones..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="lg"
                      data-testid="search-zones"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    <Button
                      kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                      size="sm"
                      hasIconOnly
                      renderIcon={GridIcon}
                      iconDescription="Card view"
                      onClick={() => setViewMode('cards')}
                    />
                    <Button
                      kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                      size="sm"
                      hasIconOnly
                      renderIcon={List}
                      iconDescription="Table view"
                      onClick={() => setViewMode('table')}
                    />
                  </div>
                </div>
                <div style={{ minWidth: '200px', flexShrink: 0, marginLeft: 'auto' }}>
                  <Dropdown
                    id="zone-type-filter"
                    titleText="Filter by Zone Type"
                    label="Select zone type"
                    items={['All Zones', 'Office', 'Room', 'Cold Room', 'Refrigerator', 'Truck', 'Esky', 'Other']}
                    selectedItem={(() => {
                      if (zoneTypeFilter === 'all') return 'All Zones';
                      const displayMap = {
                        'office': 'Office',
                        'room': 'Room',
                        'cold_room': 'Cold Room',
                        'refrigerator': 'Refrigerator',
                        'truck': 'Truck',
                        'esky': 'Esky',
                        'other': 'Other',
                      };
                      return displayMap[zoneTypeFilter] || 'All Zones';
                    })()}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        const typeMap = {
                          'All Zones': 'all',
                          'Office': 'office',
                          'Room': 'room',
                          'Cold Room': 'cold_room',
                          'Refrigerator': 'refrigerator',
                          'Truck': 'truck',
                          'Esky': 'esky',
                          'Other': 'other',
                        };
                        setZoneTypeFilter(typeMap[selectedItem] || 'all');
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {viewMode === 'cards' ? (
            // Card View
            filteredZones.map((zone) => (
              <Column lg={8} md={4} sm={4} key={zone.id}>
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    borderLeft: `4px solid ${zone.color}`,
                    padding: '0',
                    marginBottom: '16px',
                    overflow: 'hidden',
                  }}
                  data-testid={`zone-card-${zone.id}`}
                >
                  {/* Card Header */}
                  <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: zone.color, flexShrink: 0 }} />
                        <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {zone.name}
                        </h3>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {zone.alerts > 0 && (
                          <div style={{ 
                            backgroundColor: '#da1e28', 
                            color: '#ffffff', 
                            borderRadius: '12px', 
                            padding: '2px 8px', 
                            fontSize: '0.75rem', 
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minWidth: 'fit-content',
                          }}>
                            <WarningFilled size={12} /> {zone.alerts}
                          </div>
                        )}
                        {zone.createdAt && (
                          <Tag type="blue" size="sm" style={{ fontSize: '0.7rem' }}>
                            Created {formatCreatedDate(zone.createdAt)}
                          </Tag>
                        )}
                      </div>
                    </div>
                    <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>{zone.address}</p>
                  </div>

                  {/* Card Body - Stats with Visual Trends */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ padding: '16px', textAlign: 'center', borderRight: `1px solid ${borderColor}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Temperature size={16} style={{ color: '#0f62fe' }} />
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Avg Temp</p>
                      <p style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0, marginBottom: '8px' }}>{zone.avgTemp}</p>
                      <SensorStatusIndicator 
                        status={zone.tempTrend} 
                        type="temperature" 
                        showLabel={true}
                        className={isDark ? 'dark' : ''}
                      />
                    </div>
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Humidity size={16} style={{ color: '#6929c4' }} />
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Avg Humidity</p>
                      <p style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0, marginBottom: '8px' }}>{zone.avgHumidity}</p>
                      <SensorStatusIndicator 
                        status={zone.humidityTrend} 
                        type="humidity" 
                        showLabel={true}
                        className={isDark ? 'dark' : ''}
                      />
                    </div>
                  </div>

                  {/* Card Info */}
                  <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${borderColor}` }}>
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Devices</p>
                      <p style={{ color: textPrimary, fontWeight: 600, margin: 0 }}>{zone.devices}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Temp Range</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>{zone.tempRange}</p>
                    </div>
                  </div>

                  {/* Last Readings */}
                  {zone.lastReadings && zone.lastReadings.length > 0 && (
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${borderColor}` }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '8px' }}>Recent Readings</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {zone.lastReadings.map((reading, index) => (
                          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem' }}>
                            <span style={{ color: textSecondary }}>{reading.sensorName}</span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {reading.temperature !== null && (
                                <span style={{ color: '#0f62fe', fontWeight: 500 }}>
                                  {formatTemperature(reading.temperature, 'celsius')}
                                </span>
                              )}
                              {reading.humidity !== null && (
                                <span style={{ color: '#6929c4', fontWeight: 500 }}>
                                  {reading.humidity.toFixed(1)}%
                                </span>
                              )}
                              <span style={{ color: textSecondary, fontSize: '0.625rem' }}>
                                {new Date(reading.recordedAt).toLocaleDateString(undefined, { 
                                  month: 'short', 
                                  day: 'numeric'
                                })} {new Date(reading.recordedAt).toLocaleTimeString(undefined, { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sensor Tags - Horizontally scrollable on mobile */}
                  <div style={{ padding: '12px 16px', borderBottom: `1px solid ${borderColor}` }}>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '8px' }}>Sensors</p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '6px', 
                      overflowX: 'auto', 
                      paddingBottom: '4px',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}>
                      {zone.tags && zone.tags.length > 0 ? (
                        zone.tags.map((sensorName, index) => (
                          <Tag 
                            key={index} 
                            type="blue" 
                            size="sm" 
                            style={{ 
                              flexShrink: 0, 
                              maxWidth: 'none', 
                              whiteSpace: 'nowrap' 
                            }}
                          >
                            {sensorName}
                          </Tag>
                        ))
                      ) : (
                        <span style={{ color: textSecondary, fontSize: '0.75rem' }}>No sensors assigned</span>
                      )}
                    </div>
                  </div>

                  {/* Card Footer - Actions */}
                  <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    <Button kind="ghost" size="sm" renderIcon={View} onClick={() => navigate(`/dashboard/zones/${zone.id}`)} data-testid={`view-${zone.id}`}>View</Button>
                    <Button kind="ghost" size="sm" renderIcon={Edit} onClick={() => navigate(`/dashboard/zones/${zone.id}/edit`)} data-testid={`edit-${zone.id}`}>Edit</Button>
                    <Button kind="danger--ghost" size="sm" renderIcon={TrashCan} onClick={() => handleDeleteClick(zone)} data-testid={`delete-${zone.id}`}>Delete</Button>
                  </div>
                </Tile>
              </Column>
            ))
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Zone</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Devices</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Avg Temp</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Avg Humidity</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Temp Range</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Sensors</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Created</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Alerts</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedZones.map((zone, idx) => (
                      <tr
                        key={zone.id}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                          backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                        }}
                        data-testid={`zone-row-${zone.id}`}
                      >
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: zone.color }} />
                            <div>
                              <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '2px' }}>{zone.name}</p>
                              <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>{zone.address}</p>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600 }}>{zone.devices}</td>
                        <td style={{ padding: '16px', textAlign: 'center', color: '#0f62fe', fontWeight: 600 }}>{zone.avgTemp}</td>
                        <td style={{ padding: '16px', textAlign: 'center', color: '#6929c4', fontWeight: 600 }}>{zone.avgHumidity}</td>
                        <td style={{ padding: '16px', color: textSecondary }}>{zone.tempRange}</td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {zone.tags && zone.tags.length > 0 ? (
                              zone.tags.map((sensorName, index) => (
                                <Tag key={index} type="blue" size="sm" style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
                                  {sensorName}
                                </Tag>
                              ))
                            ) : (
                              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>No sensors</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>
                          {zone.createdAt ? (
                            <Tag type="blue" size="sm">{formatCreatedDate(zone.createdAt)}</Tag>
                          ) : (
                            <span style={{ color: textSecondary }}>Unknown</span>
                          )}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          {zone.alerts > 0 ? (
                            <Tag type="red" size="sm">{zone.alerts}</Tag>
                          ) : (
                            <Tag type="green" size="sm">None</Tag>
                          )}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" onClick={() => navigate(`/dashboard/zones/${zone.id}`)} />
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={Edit} iconDescription="Edit" onClick={() => navigate(`/dashboard/zones/${zone.id}/edit`)} />
                            <Button kind="danger--ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Delete" onClick={() => handleDeleteClick(zone)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - only show for table view */}
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageNumberText="Page Number"
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>
    </div>
    </SubscriptionGate>
  );
};

export default ZoneListPage;
